import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiHome,
  HiAcademicCap,
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiUserCircle,
  HiInbox,
  HiHeart,
  HiCurrencyDollar,
  HiChatBubbleLeftRight,
  HiDocumentChartBar,
  HiUserGroup,
  HiMegaphone,
  HiClipboardDocumentList,
  HiUserPlus,
  HiChevronDown,
  HiScale,
  HiChartBar,
  HiDocumentText,
  HiBriefcase,
  HiCheckCircle,
  HiRocketLaunch,
  HiTrash,
} from 'react-icons/hi2';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../../database/firebase';
import { doc, getDoc } from 'firebase/firestore';

const navLinks = [
  { label: 'Dashboard', icon: HiHome, key: 'crm', color: 'from-blue-500 to-blue-600' },
  {
    label: 'Document Bank',
    icon: HiDocumentText,
    key: 'document-bank',
    color: 'from-blue-500 to-blue-600',
  },
  {
    label: 'Programs',
    icon: HiAcademicCap,
    key: 'programs',
    color: 'from-blue-500 to-blue-600',
    submenu: [
      { label: 'Training', key: 'training', icon: HiAcademicCap },
      { label: 'Product Development', key: 'product-development', icon: HiRocketLaunch },
      {
        label: 'Commercialisation',
        key: 'commercialisation',
        icon: HiBriefcase,
        submenu: [
          {
            label: 'Commercialisation Readiness Checklist',
            key: 'commercialisation-readiness-checklist',
            icon: HiCheckCircle,
          },
          {
            label: 'Go to Market Strategy and Commercialisation Plan',
            key: 'go-to-market-strategy',
            icon: HiChartBar,
          },
          {
            label: 'Start-Up Launch Checklist',
            key: 'startup-launch-checklist',
            icon: HiCheckCircle,
          },
          {
            label: 'Admin Panel',
            key: 'commercialisation-admin-panel',
            icon: HiUserGroup,
          },
        ],
      },
      { label: 'Scaling', key: 'scalingmodules', icon: HiScale },
      { label: 'Program Health Check', key: 'program-health-check', icon: HiHeart },
      { label: 'Funding', key: 'funding', icon: HiCurrencyDollar },
    ],
  },

  {
    label: 'Analytics',
    icon: HiChartBar,
    key: 'analytics',
    color: 'from-green-500 to-green-600',
    submenu: [
      {
        label: 'Profiling Analytics',
        key: 'profiling-analytics',
        icon: HiClipboardDocumentList,
      },
      {
        label: 'Commercialisation Analytics',
        key: 'commercialisation-analytics',
        icon: HiBriefcase,
      },
    ],
  },

  {
    label: 'Profiling',
    icon: HiClipboardDocumentList,
    key: 'profiling',
    color: 'from-green-500 to-green-600',
    submenu: [{ label: 'Business Health Check', key: 'business-health', icon: HiHeart }],
  },

  {
    label: 'Inbox',
    icon: HiChatBubbleLeftRight,
    key: 'communication',
    color: 'from-blue-500 to-blue-600',
    submenu: [
      { label: 'Public Chat', key: 'public', icon: HiChatBubbleLeftRight },
      { label: 'Private Inbox', key: 'private', icon: HiInbox },
    ],
  },

  {
    label: 'Reports',
    icon: HiChatBubbleLeftRight,
    key: 'communication-2',
    color: 'from-blue-500 to-blue-600',
    submenu: [
      { label: 'Reports', key: 'reports', icon: HiDocumentChartBar },
      { label: 'Announcements', key: 'announcements', icon: HiMegaphone },
      { label: 'Weekly Reports', key: 'weekly-reports', icon: HiDocumentChartBar },
      {
        label: 'Monthly Coaching Reports',
        key: 'monthly-coaching-reports',
        icon: HiDocumentChartBar,
      },
    ],
  },

  {
    label: 'User Management',
    icon: HiUserGroup,
    key: 'user-management',
    color: 'from-blue-500 to-blue-600',
    submenu: [
      { label: 'Clients', key: 'clients', icon: HiUserPlus },
      { label: 'Alumni', key: 'alumni', icon: HiUserGroup },
      { label: 'Applications', key: 'applications', icon: HiClipboardDocumentList },
      { label: 'Archives', key: 'junk-management', icon: HiTrash },
    ],
  },
];

const ModeratorSidebar = ({
  collapsed,
  setCollapsed,
  selected,
  setSelected,
  inboxSubsection,
  onInboxSubsectionChange,
}) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(firestore, 'moderator', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Auto-expand parent menus when nested items are selected
  useEffect(() => {
    navLinks.forEach(link => {
      if (link.submenu) {
        const hasSelectedSubmenu = link.submenu.some(subItem => {
          if (subItem.submenu) {
            return subItem.submenu.some(nestedItem => selected === nestedItem.key);
          }
          return selected === subItem.key;
        });
        if (hasSelectedSubmenu) {
          setExpandedMenus(prev => ({ ...prev, [link.key]: true }));
          // Also expand the parent submenu if a nested item is selected
          link.submenu.forEach(subItem => {
            if (
              subItem.submenu &&
              subItem.submenu.some(nestedItem => selected === nestedItem.key)
            ) {
              setExpandedMenus(prev => ({ ...prev, [subItem.key]: true }));
            }
          });
        }
      }
    });
  }, [selected]);

  const displayData = {
    firstName: userProfile?.firstname || user?.displayName?.split(' ')[0] || 'Business Advisor',
    lastName: userProfile?.lastname || user?.displayName?.split(' ')[1] || '',
    email: user?.email || '',
  };

  const initials =
    `${displayData.firstName.charAt(0)}${displayData.lastName.charAt(0)}`.toUpperCase();

  const toggleSubmenu = key => {
    setExpandedMenus(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const sidebarVariants = {
    expanded: { width: 280 },
    collapsed: { width: 80 },
  };

  const contentVariants = {
    expanded: { opacity: 1, x: 0 },
    collapsed: { opacity: 0, x: -20 },
  };

  const handleMenuClick = link => {
    if (link.submenu) {
      toggleSubmenu(link.key);
    } else {
      setSelected(link.key);
    }
  };

  const handleSubmenuClick = (subItem, parentKey) => {
    // If subItem has its own submenu, toggle it instead of selecting
    if (subItem.submenu && subItem.submenu.length > 0) {
      toggleSubmenu(subItem.key);
      return;
    }

    if (parentKey === 'communication') {
      onInboxSubsectionChange(subItem.key);
      setSelected('communication');
    } else {
      setSelected(subItem.key);
    }
  };

  return (
    <motion.aside
      variants={sidebarVariants}
      animate={collapsed ? 'collapsed' : 'expanded'}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="flex h-full flex-col border-r border-gray-200 bg-white shadow-lg"
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCollapsed(!collapsed)}
          className="rounded-xl p-2 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <motion.div animate={{ rotate: collapsed ? 0 : 180 }} transition={{ duration: 0.3 }}>
            {collapsed ? (
              <HiChevronDoubleRight className="h-5 w-5 text-gray-600" />
            ) : (
              <HiChevronDoubleLeft className="h-5 w-5 text-gray-600" />
            )}
          </motion.div>
        </motion.button>
      </div>
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            variants={contentVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            transition={{ duration: 0.2, delay: 0.15 }}
            className="border-b border-gray-200 px-4 py-6"
          >
            {loading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200"></div>
                <div className="space-y-2">
                  <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                  <div className="h-3 w-24 animate-pulse rounded bg-gray-200"></div>
                </div>
              </div>
            ) : user ? (
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700 shadow-lg">
                    {initials}
                  </div>
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-green-500"></div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-semibold text-black">
                    {displayData.firstName} {displayData.lastName}
                  </div>
                  <div className="max-w-full truncate text-xs text-gray-500">
                    {displayData.email}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <HiUserCircle className="h-12 w-12 text-gray-300" />
                <span className="text-sm text-gray-400">Not logged in</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-2">
          {navLinks.map((link, index) => {
            const Icon = link.icon;
            const isSelected =
              selected === link.key ||
              (link.submenu &&
                link.submenu.some(subItem => {
                  if (subItem.submenu) {
                    return subItem.submenu.some(nestedItem => selected === nestedItem.key);
                  }
                  return selected === subItem.key;
                }));
            const isHovered = hoveredItem === link.key;
            const hasSubmenu = link.submenu && link.submenu.length > 0;
            const isExpanded = expandedMenus[link.key];

            return (
              <div key={link.key}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleMenuClick(link)}
                    onHoverStart={() => setHoveredItem(link.key)}
                    onHoverEnd={() => setHoveredItem(null)}
                    className={[
                      'relative flex w-full items-center justify-between rounded-xl px-3 py-3 text-left transition-all duration-200',
                      isSelected
                        ? 'border-l-4 border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : '',
                      isHovered
                        ? 'hover:bg-gray-50 hover:text-black'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-black',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <Icon
                          className={`h-5 w-5 flex-shrink-0 transition-colors ${isSelected ? 'text-blue-500' : isHovered ? 'text-black' : 'text-gray-400'}`}
                        />
                        {collapsed && isHovered && (
                          <motion.div
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="absolute left-8 top-1/2 z-50 -translate-y-1/2 rounded-lg bg-black px-2 py-1 text-xs text-white shadow-lg"
                          >
                            {link.label}
                            <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-black"></div>
                          </motion.div>
                        )}
                      </div>
                      <AnimatePresence>
                        {!collapsed && (
                          <motion.span
                            variants={contentVariants}
                            initial="collapsed"
                            animate="expanded"
                            exit="collapsed"
                            transition={{ duration: 0.2 }}
                            className={`text-sm ${isSelected ? 'font-bold text-black' : 'font-medium'}`}
                          >
                            {link.label}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                    {hasSubmenu && !collapsed && (
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex-shrink-0"
                      >
                        <HiChevronDown
                          className={`h-3.5 w-3.5 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}
                        />
                      </motion.div>
                    )}
                  </motion.button>
                </motion.div>

                {hasSubmenu && isExpanded && !collapsed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-6 mt-1 space-y-0.5 border-l-2 border-gray-100 pl-2"
                  >
                    {link.submenu.map(subItem => {
                      const SubIcon = subItem.icon;
                      const isSubSelected =
                        link.key === 'communication'
                          ? selected === 'communication' && inboxSubsection === subItem.key
                          : selected === subItem.key;
                      const hasNestedSubmenu = subItem.submenu && subItem.submenu.length > 0;
                      const isNestedExpanded = expandedMenus[subItem.key];

                      return (
                        <div key={subItem.key}>
                          <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() => handleSubmenuClick(subItem, link.key)}
                            className={`
                              flex w-full items-center justify-between gap-3 rounded-lg border-l-2 px-3 py-2 text-sm transition-all duration-200
                              ${isSubSelected ? 'border-blue-500 bg-blue-50 font-medium text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-black'}
                              ${hasNestedSubmenu && subItem.key !== 'commercialisation' ? 'font-medium' : ''}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              <SubIcon
                                className={`h-4 w-4 flex-shrink-0 ${isSubSelected ? 'text-blue-600' : 'text-gray-500'}`}
                              />
                              <span className="truncate">{subItem.label}</span>
                            </div>
                            {hasNestedSubmenu && (
                              <motion.div
                                animate={{ rotate: isNestedExpanded ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex-shrink-0"
                              >
                                <HiChevronDown
                                  className={`h-3.5 w-3.5 ${isSubSelected ? 'text-blue-600' : 'text-gray-400'}`}
                                />
                              </motion.div>
                            )}
                          </motion.button>

                          {hasNestedSubmenu && isNestedExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="ml-4 mt-1 space-y-0.5 border-l-2 border-gray-200 pl-3"
                            >
                              {subItem.submenu.map((nestedItem, nestedIndex) => {
                                const NestedIcon = nestedItem.icon;
                                const isNestedSelected = selected === nestedItem.key;

                                return (
                                  <motion.button
                                    key={nestedItem.key}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: nestedIndex * 0.05 }}
                                    whileHover={{ scale: 1.01, x: 2 }}
                                    whileTap={{ scale: 0.99 }}
                                    onClick={() => handleSubmenuClick(nestedItem, subItem.key)}
                                    className={`
                                      flex w-full items-center gap-2.5 rounded-md border-l-2 px-2.5 py-1.5 text-xs transition-all duration-200
                                      ${isNestedSelected ? 'border-blue-500 bg-blue-50 font-medium text-blue-700' : 'border-gray-200 text-gray-600 hover:translate-x-1 hover:border-gray-300 hover:bg-gray-50 hover:text-black'}
                                    `}
                                  >
                                    <NestedIcon
                                      className={`h-3.5 w-3.5 flex-shrink-0 ${isNestedSelected ? 'text-blue-600' : 'text-gray-400'}`}
                                    />
                                    <span className="truncate text-xs">{nestedItem.label}</span>
                                  </motion.button>
                                );
                              })}
                            </motion.div>
                          )}
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </nav>
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            variants={contentVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200 px-4 py-4"
          >
            <div className="text-center text-xs text-gray-400">© 2025</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.aside>
  );
};

export default ModeratorSidebar;
